import {Component, OnInit} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {AccountService} from "../shared/services/account.service";
import {NotificationService} from "../shared/services/notification.service";
import {AuthService} from "../shared/services/auth.service";
import {filter, firstValueFrom} from "rxjs";
import {AuthenticatedUser} from "../shared/dm";
import {FIREBASE_AUTH_PROVIDERS} from "../sign-in-methods/auth-providers";

export namespace CommunicationForm {
  export const FIELD_EMAIL = "email"
}

interface EmailOption {
  email: string
  providerName: string | null
  providerIcon: string | null
  verified: boolean
}

const MSG_COMMUNICATION_SAVED = $localize `:@@section.communication.saved:You have successfully updated your contact email address.`

@Component({
  selector: "communication",
  templateUrl: "communication.component.html",
  styleUrls: ["communication.component.scss"]
})
export class CommunicationComponent implements OnInit {
  constructor(
    private readonly accountService: AccountService,
    private readonly notificationService: NotificationService,
    private readonly authService: AuthService
  ) {}

  loading = false
  saving = false

  emailOptions: EmailOption[] = []

  form = new FormGroup({
    [CommunicationForm.FIELD_EMAIL]: new FormControl(null, Validators.required)
  })

  CommunicationForm = CommunicationForm

  get hasVerifiedOptions(): boolean {
    return this.emailOptions.some(o => o.verified)
  }

  async ngOnInit(): Promise<void> {
    this.loading = true

    try {
      const user = await firstValueFrom(
        this.authService.user$.pipe(
          filter((u): u is AuthenticatedUser => u !== undefined)
        )
      )
      const [providers, verifiedEmails] = await Promise.all([
        firstValueFrom(this.accountService.getLinkedProviders()),
        firstValueFrom(this.accountService.loadVerifiedEmails()),
      ])

      const seenEmails = new Set<string>()
      this.emailOptions = providers
        .filter(p => p.email != null)
        .map(p => {
          seenEmails.add(p.email!)
          const authProvider = FIREBASE_AUTH_PROVIDERS.find(ap => ap.id === p.providerId)
          return {
            email: p.email!,
            providerName: authProvider?.name ?? null,
            providerIcon: authProvider?.icon ?? null,
            verified: verifiedEmails.includes(p.email!),
          }
        })

      // Edge case: current contact email is not linked to any provider
      if (!seenEmails.has(user.email)) {
        this.emailOptions.push({ email: user.email, providerName: null, providerIcon: null, verified: true })
      }

      this.emailOptions.sort((a, b) => (a.verified === b.verified ? 0 : a.verified ? -1 : 1))

      this.form.patchValue({
        [CommunicationForm.FIELD_EMAIL]: user.email
      })
    } finally {
      this.loading = false
    }
  }

  async save(): Promise<void> {
    const selectedEmail = this.form.value[CommunicationForm.FIELD_EMAIL]

    try {
      this.saving = true
      await firstValueFrom(this.accountService.updateContactEmail(selectedEmail))
      const user = await firstValueFrom(this.authService.user$)

      this.authService.updateUser({
        ...user!!,
        email: selectedEmail
      })

      this.notificationService.showSuccessMessage(MSG_COMMUNICATION_SAVED)
    } finally {
      this.saving = false
    }
  }
}
